import { createSelector } from 'reselect';
import { array, number, ord, boolean, option, monoid } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { createAction } from '@mews/framework';
import { ApplicationState, AgeCategory } from '@mews-commander/types';
import { getContainer } from '@mews-commander/legacy-app-instance';
import { createOptionsSelector } from '@mews-commander/utils';
import moduleSelector from '../selector';
import prefix from '../prefix';
import { createEntitiesReducer, createBaseEntitySelectors, createEntitySelectorByServiceId } from './utils';

export const NAME = 'ageCategories';

const DEFAULT_MINIMAL_AGE = 0;

const AGE_CATEGORIES_LOADED = prefix('AGE_CATEGORIES_LOADED');

const ageCategoryMonoid = ord.getMonoid<AgeCategory>();

export const sortAgeCategoriesOrd = monoid.concatAll(ageCategoryMonoid)([
    pipe(
        ord.reverse(boolean.Ord),
        ord.contramap(({ isDefault }) => isDefault)
    ),
    pipe(
        number.Ord,
        ord.contramap(({ minimalAge }) => minimalAge ?? DEFAULT_MINIMAL_AGE)
    ),
]);

export const sortAgeCategories = array.sort<AgeCategory>(sortAgeCategoriesOrd);

export const sortByAgeCategoryOrd = <T extends { ageCategoryId?: string | null | undefined }>(
    ageCategories: AgeCategory[]
) =>
    pipe(
        number.Ord,
        ord.contramap((a: T) => ageCategories.findIndex((category) => category.id === a.ageCategoryId))
    );

export const setAgeCategories = (entities: AgeCategory[]) => createAction(AGE_CATEGORIES_LOADED, { entities });

export const ageCategoriesReducer = createEntitiesReducer({ FETCH_SUCCESS: AGE_CATEGORIES_LOADED });

export const [getAgeCategories, getAgeCategoriesByIds, getAgeCategoryByOwnerId, getAgeCategory] =
    createBaseEntitySelectors<ApplicationState, AgeCategory, typeof NAME>(moduleSelector, NAME);

const getAgeCategoriesByServiceId = createEntitySelectorByServiceId(getAgeCategories);

export const getAgeCategoryIdsByServiceId = createSelector(
    getAgeCategoriesByServiceId,
    array.map((ageCategory) => ageCategory.id)
);

export const getServiceDefaultAgeCategoryId = createSelector(getAgeCategoriesByServiceId, (categories) =>
    pipe(
        categories,
        array.findFirstMap<AgeCategory, string>(({ isDefault, id }) => (isDefault ? option.some(id) : option.none)),
        option.toUndefined
    )
);

export const getSortedAgeCategoriesByServiceId = createSelector(getAgeCategoriesByServiceId, sortAgeCategories);

export const getSortedAgeCategoryIdsByServiceId = createSelector(
    getSortedAgeCategoriesByServiceId,
    array.map((ageCategory) => ageCategory.id)
);

export const getSortedActiveAgeCategoryIdsByServiceId = createSelector(
    getSortedAgeCategoriesByServiceId,
    (categories) =>
        pipe(
            categories,
            array.filter(({ isDeleted }) => !isDeleted),
            array.map(({ id }) => id)
        )
);

const getAgeCategoryOption = createSelector(getAgeCategory, option.fromNullable);

const getAgeCategoryName = createSelector(
    getAgeCategoryOption,
    option.map(({ name }) => name)
);

const getAgeCategoryShortName = createSelector(
    getAgeCategoryOption,
    option.map(({ shortName }) => shortName)
);

export const getAgeCategoryLocalizedName = createSelector(getContainer, getAgeCategoryName, (container, name) =>
    container().appLocalizationComponent.localizeProperty(option.toNullable(name))
);

export const getAgeCategoryLocalizedShortName = createSelector(
    getContainer,
    getAgeCategoryShortName,
    (container, shortName) => container().appLocalizationComponent.localizeProperty(option.toNullable(shortName))
);

export const getSortedAgeCategorySelectOptionsByIds = createOptionsSelector(getAgeCategoriesByIds, {
    labelLocalizerName: 'localizeProperty',
    // @ts-expect-error $TSFixMeError: Type '<A extends AgeCategory>(as: A[]) => A[]' is not assignable to type 'LodashIdentity'.
    sortFunction: sortAgeCategories,
});
